import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  generate810,
  get810Batch,
  get810Workflow,
  prepare810Submission,
  send810Submission,
} from "../utils/ediApi";
import { localBusinessDate } from "./Walmart810Reconciliation";

export const BATCH_810_POLL_STATES = new Set(["SUBMITTING", "AWAITING_MDN", "AWAITING_997"]);
const SAFE_AFTER_SEND = new Set(["AWAITING_MDN", "AWAITING_997", "COMPLETED"]);
const TERMINAL_STOP_STATES = new Set(["WALMART_REJECTED", "IT_ATTENTION_REQUIRED"]);
const NEVER_SEND_STATES = new Set(["SUBMITTING", "AWAITING_MDN", "AWAITING_997", "COMPLETED"]);

const shown = (value) => value === null || value === undefined || value === "" ? "—" : String(value);
const issueLabel = (value) => String(value || "Unknown blocker").toLowerCase().replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

export function batch810StateLabel(state) {
  return {
    READY_TO_GENERATE: "Ready to Generate",
    READY_TO_REVIEW: "Ready to Review",
    READY_TO_SEND: "Ready to Send",
    GENERATED_UNSENT: "Generated, Unsent",
    SUBMITTING: "Submitting",
    AWAITING_MDN: "Waiting for AS2 Confirmation",
    AWAITING_997: "Waiting for Walmart 997",
    COMPLETED: "Accepted",
    ACCEPTED: "Accepted",
    PREVIEW_BLOCKED: "Blocked",
    BLOCKED: "Blocked",
    WALMART_REJECTED: "Walmart Rejected",
    IT_ATTENTION_REQUIRED: "IT Attention Required",
  }[state] || shown(state);
}

export function authoritativeQuantity(lines) {
  if (!Array.isArray(lines) || !lines.length) return null;
  const values = lines.map((line) => {
    const raw = line?.shippedQuantity;
    if (raw === null || raw === undefined || String(raw).trim() === "") return null;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : null;
  });
  return values.every((value) => value !== null) ? values.reduce((total, value) => total + value, 0) : null;
}

export function preparedTraceFromSubmission(submission) {
  return {
    generationId: submission?.generationId,
    invoiceNumber: submission?.invoiceNumber,
    invoiceDate: submission?.invoiceDate,
    controlNumbers: submission?.controlNumbers ? { ...submission.controlNumbers } : undefined,
  };
}

export function is810Selectable(row, full = true) {
  if (!full) return false;
  const state = row?.workflowState;
  const actions = row?.actions || {};
  return (state === "READY_TO_GENERATE" && actions.canGenerate === true)
    || (["READY_TO_REVIEW", "READY_TO_SEND"].includes(state) && actions.canPrepare === true);
}

function ReviewModal({ rows, busy, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  useEffect(() => { cancelRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape" && !busy) onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
    <section role="dialog" aria-modal="true" aria-labelledby="batch-810-review-title" className="max-h-[90vh] w-full max-w-7xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
      <h2 id="batch-810-review-title" className="text-xl font-bold">Review Selected 810s Before Sending</h2>
      <p className="mt-2 text-sm text-slate-600">Review the exact server-prepared documents below. Nothing has been sent yet.</p>
      <div className="mt-4 overflow-x-auto"><table className="min-w-full text-left text-sm">
        <thead><tr className="border-b bg-slate-50"><th className="p-2">PO</th><th className="p-2">Invoice Number</th><th className="p-2">Invoice Date</th><th className="p-2">Quantity</th><th className="p-2">Gross</th><th className="p-2">C000</th><th className="p-2">I570</th><th className="p-2">Invoice Total</th><th className="p-2">ISA13</th><th className="p-2">GS06</th><th className="p-2">ST02</th></tr></thead>
        <tbody>{rows.map(({ poNumber, submission }) => <tr key={poNumber} className="border-b"><td className="p-2 font-medium">{shown(submission.poNumber)}</td><td className="p-2">{shown(submission.invoiceNumber)}</td><td className="p-2">{shown(submission.invoiceDate)}</td><td className="p-2">{shown(submission.quantity)}</td><td className="p-2">{shown(submission.gross)}</td><td className="p-2">{shown(submission.c000)}</td><td className="p-2">{shown(submission.i570)}</td><td className="p-2">{shown(submission.invoiceTotal)}</td><td className="p-2">{shown(submission.controlNumbers?.isa13)}</td><td className="p-2">{shown(submission.controlNumbers?.gs06)}</td><td className="p-2">{shown(submission.controlNumbers?.st02)}</td></tr>)}</tbody>
      </table></div>
      <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900"><p className="font-semibold">Each document will be sent once.</p><p className="mt-1 font-semibold">Do not retry any document with an unknown transmission outcome.</p></div>
      <div className="mt-5 flex justify-end gap-3"><button ref={cancelRef} type="button" disabled={busy} onClick={onCancel} className="rounded border px-4 py-2 disabled:opacity-50">Cancel</button><button type="button" disabled={busy} onClick={onConfirm} className="rounded bg-red-700 px-4 py-2 text-white disabled:opacity-50">{busy ? "Working..." : `Send ${rows.length} 810s to Walmart`}</button></div>
    </section>
  </div>;
}

function StatusTable({ rows, preparedTrace }) {
  if (!rows.length) return null;
  return <section className="rounded-lg border bg-white p-4"><h2 className="text-lg font-semibold">Batch Status</h2><div className="mt-3 overflow-x-auto"><table className="min-w-full text-left text-sm">
    <thead><tr className="border-b bg-slate-50"><th className="p-2">PO</th><th className="p-2">Invoice #</th><th className="p-2">Generation</th><th className="p-2">ISA13</th><th className="p-2">ST02</th><th className="p-2">Transport</th><th className="p-2">Walmart 997</th><th className="p-2">Final Status</th></tr></thead>
    <tbody>{rows.map((row) => {
      const state = row.workflowState;
      const trace = preparedTrace[row.poNumber] || {};
      const walmart997 = state === "AWAITING_997" ? "Waiting" : state === "COMPLETED" ? "Accepted" : state === "WALMART_REJECTED" ? "Rejected" : "—";
      return <tr key={row.poNumber} className="border-b"><td className="p-2 font-medium">{shown(row.poNumber)}</td><td className="p-2">{shown(row.generation?.invoiceNumber || trace.invoiceNumber)}</td><td className="p-2">{shown(row.generation?.generationId || trace.generationId)}</td><td className="p-2">{shown(trace.controlNumbers?.isa13)}</td><td className="p-2">{shown(trace.controlNumbers?.st02)}</td><td className="p-2">{shown(row.submission?.transportState)}</td><td className="p-2">{walmart997}</td><td className="p-2">{batch810StateLabel(state)}</td></tr>;
    })}</tbody>
  </table></div></section>;
}

export default function Walmart810Batch({ full, headers, onPermissionDenied, onMutationChange, initialLoadId = "" }) {
  const [loadId, setLoadId] = useState(String(initialLoadId || ""));
  const [invoiceDate, setInvoiceDate] = useState(localBusinessDate());
  const [activeLoadId, setActiveLoadId] = useState("");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [runPoNumbers, setRunPoNumbers] = useState([]);
  const [preparedTrace, setPreparedTrace] = useState({});
  const [reviewRows, setReviewRows] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [statusVisible, setStatusVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [error, setError] = useState("");
  const [halted, setHalted] = useState(false);
  const [writeDenied, setWriteDenied] = useState(false);
  const mounted = useRef(true);
  const requestId = useRef(0);
  const operationGeneration = useRef(0);
  const mutationActive = useRef(false);
  const pollActive = useRef(false);
  const canWrite = full && !writeDenied;
  const fullRef = useRef(canWrite);
  const previousFull = useRef(canWrite);
  const lastInitialLoadId = useRef("");
  const mutationChangeRef = useRef(onMutationChange);
  fullRef.current = canWrite;
  mutationChangeRef.current = onMutationChange;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false; requestId.current += 1; operationGeneration.current += 1;
      mutationActive.current = false; pollActive.current = false; mutationChangeRef.current?.(false);
    };
  }, []);

  useEffect(() => {
    if (previousFull.current && !canWrite) {
      operationGeneration.current += 1; mutationActive.current = false;
      setSelected([]); setReviewRows([]); setReviewOpen(false); setBusyAction(null);
      setError("Your Walmart 810 permission changed. The workflow is now read-only.");
      mutationChangeRef.current?.(false);
    }
    previousFull.current = canWrite;
  }, [canWrite]);

  const beginMutation = (action) => {
    const operation = ++operationGeneration.current;
    mutationActive.current = true; setBusyAction(action); mutationChangeRef.current?.(true);
    return operation;
  };
  const operationIsActive = (operation) => mounted.current && mutationActive.current && operationGeneration.current === operation && fullRef.current;
  const finishMutation = (operation) => {
    if (operationGeneration.current !== operation) return;
    mutationActive.current = false;
    if (mounted.current) { setBusyAction(null); mutationChangeRef.current?.(false); }
  };

  const replaceWorkflow = useCallback((poNumber, workflow, expectedLoad = null, expectedRequest = null) => {
    if (!mounted.current) return;
    if (expectedLoad !== null && expectedLoad !== activeLoadId) return;
    if (expectedRequest !== null && expectedRequest !== requestId.current) return;
    setRows((current) => current.map((row) => row.poNumber === poNumber ? workflow : row));
  }, [activeLoadId]);

  const loadBatch = useCallback(async (requestedLoadId) => {
    const normalized = String(requestedLoadId || "").trim();
    if (!normalized || mutationActive.current) return false;
    const id = ++requestId.current;
    setLoading(true); setError(""); setReviewRows([]); setReviewOpen(false); setSelected([]);
    setRunPoNumbers([]); setPreparedTrace({}); setStatusVisible(false); setHalted(false);
    try {
      const value = await get810Batch(normalized, { headers });
      if (!mounted.current || id !== requestId.current) return false;
      setRows(Array.isArray(value?.rows) ? value.rows : []); setActiveLoadId(value?.loadId || normalized);
      return true;
    } catch (exception) {
      if (mounted.current && id === requestId.current) {
        setRows([]); setActiveLoadId(""); setError(exception?.message || "Unable to load the Walmart 810 batch.");
      }
      return false;
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    const normalized = String(initialLoadId || "").trim();
    if (!normalized || lastInitialLoadId.current === normalized) return;
    lastInitialLoadId.current = normalized; setLoadId(normalized); loadBatch(normalized);
  }, [initialLoadId, loadBatch]);

  const permissionFailure = useCallback(async (exception) => {
    if (exception?.status !== 403 && exception?.errorCode !== "FORBIDDEN") return false;
    fullRef.current = false; operationGeneration.current += 1; mutationActive.current = false;
    if (mounted.current) {
      setWriteDenied(true);
      setSelected([]); setReviewRows([]); setReviewOpen(false); setBusyAction(null);
      setError("Your Walmart 810 permission changed. The workflow is now read-only.");
      mutationChangeRef.current?.(false);
      await Promise.resolve().then(() => onPermissionDenied?.()).catch(() => {});
    }
    return true;
  }, [onPermissionDenied]);

  const selectable = useMemo(() => rows.filter((row) => is810Selectable(row, canWrite)).map((row) => row.poNumber), [canWrite, rows]);
  const allSelected = selectable.length > 0 && selectable.every((po) => selected.includes(po));
  const toggle = (poNumber) => setSelected((current) => current.includes(poNumber) ? current.filter((value) => value !== poNumber) : [...current, poNumber]);
  const changeLoadId = (nextLoadId) => {
    requestId.current += 1;
    setLoadId(nextLoadId); setActiveLoadId(""); setRows([]); setSelected([]); setRunPoNumbers([]);
    setPreparedTrace({}); setReviewRows([]); setReviewOpen(false); setStatusVisible(false); setHalted(false);
    setLoading(false); setError("");
  };

  const generateAndReview = async () => {
    if (mutationActive.current || !fullRef.current || !activeLoadId || !invoiceDate) return;
    const targets = rows.filter((row) => selected.includes(row.poNumber));
    if (!targets.length) { setError("Select at least one eligible PO."); return; }
    const operation = beginMutation("prepare");
    setError(""); setHalted(false); setStatusVisible(false); setRunPoNumbers(targets.map((row) => row.poNumber));
    const prepared = [];
    let activePo = targets[0].poNumber;
    try {
      for (const target of targets) {
        activePo = target.poNumber;
        if (!operationIsActive(operation)) return;
        let workflow = await get810Workflow(activeLoadId, target.poNumber, { headers });
        if (!operationIsActive(operation)) return;
        replaceWorkflow(target.poNumber, workflow);
        if (workflow.workflowState === "READY_TO_GENERATE") {
          if (workflow.generation || workflow.actions?.canGenerate !== true) throw new Error("The invoice workflow changed. Refresh the batch before continuing.");
          await generate810(activeLoadId, target.poNumber, invoiceDate, { headers });
          if (!operationIsActive(operation)) return;
          workflow = await get810Workflow(activeLoadId, target.poNumber, { headers });
          if (!operationIsActive(operation)) return;
          replaceWorkflow(target.poNumber, workflow);
        }
        if (!["READY_TO_REVIEW", "READY_TO_SEND"].includes(workflow.workflowState)
            || workflow.actions?.canPrepare !== true || !workflow.generation?.generationId) {
          throw new Error("The invoice is no longer ready for review.");
        }
        const submission = await prepare810Submission(workflow.generation.generationId, { headers });
        if (!operationIsActive(operation)) return;
        if (!submission?.confirmationToken || submission.generationId !== workflow.generation.generationId) throw new Error("The prepared invoice identity changed. Refresh the batch before continuing.");
        prepared.push({ poNumber: target.poNumber, generationId: workflow.generation.generationId, submission });
      }
      if (mounted.current) {
        setPreparedTrace((current) => ({ ...current, ...Object.fromEntries(prepared.map((row) => [row.poNumber, preparedTraceFromSubmission(row.submission)])) }));
        setReviewRows(prepared); setReviewOpen(true);
      }
    } catch (exception) {
      const denied = await permissionFailure(exception);
      if (!denied && operationIsActive(operation)) {
        setReviewRows([]); setReviewOpen(false);
        setError(`${activePo}: ${exception?.message || "The Walmart 810 could not be prepared safely."}`);
      }
    } finally { finishMutation(operation); }
  };

  const stopBatch = (poNumber, message) => {
    if (!mounted.current) return;
    setHalted(true); setError(`${poNumber}: Batch stopped for safety. ${message}`);
  };

  const sendSelected = async () => {
    if (mutationActive.current || !fullRef.current || !reviewRows.length) return;
    const targets = [...reviewRows];
    const operation = beginMutation("send");
    setError(""); setHalted(false); setStatusVisible(true); setReviewOpen(false); setReviewRows([]);
    try {
      for (const target of targets) {
        if (!operationIsActive(operation)) return;
        let workflow = await get810Workflow(activeLoadId, target.poNumber, { headers });
        if (!operationIsActive(operation)) return;
        replaceWorkflow(target.poNumber, workflow);
        if (workflow.workflowState === "SUBMITTING") { stopBatch(target.poNumber, "This invoice is still submitting. No remaining invoices were sent."); break; }
        if (TERMINAL_STOP_STATES.has(workflow.workflowState)) { stopBatch(target.poNumber, "This invoice requires review. No remaining invoices were sent."); break; }
        if (NEVER_SEND_STATES.has(workflow.workflowState)) continue;
        if (!["READY_TO_REVIEW", "READY_TO_SEND"].includes(workflow.workflowState)
            || workflow.actions?.canPrepare !== true
            || workflow.generation?.generationId !== target.generationId
            || target.submission?.generationId !== target.generationId) {
          stopBatch(target.poNumber, "The prepared invoice identity or workflow changed. Refresh the batch before continuing."); break;
        }
        let submission;
        try {
          submission = await send810Submission(target.generationId, target.submission.confirmationToken, { headers });
        } catch (exception) {
          if (!operationIsActive(operation)) return;
          if (await permissionFailure(exception)) break;
          try {
            workflow = await get810Workflow(activeLoadId, target.poNumber, { headers });
            if (operationIsActive(operation)) replaceWorkflow(target.poNumber, workflow);
          } catch { /* preserve the original ambiguous result */ }
          const message = exception?.errorCode === "TRANSPORT_OUTCOME_UNKNOWN"
            ? "The transmission outcome is unknown. Do not resend this invoice."
            : "The transmission may require IT attention. Do not resend this invoice.";
          stopBatch(target.poNumber, message); break;
        }
        if (!operationIsActive(operation)) return;
        if (submission?.state === "SUBMITTING") {
          replaceWorkflow(target.poNumber, { ...workflow, workflowState: "SUBMITTING", submission });
          stopBatch(target.poNumber, "This invoice is still submitting. No remaining invoices were sent."); break;
        }
        if (!SAFE_AFTER_SEND.has(submission?.state)) {
          replaceWorkflow(target.poNumber, { ...workflow, workflowState: submission?.state || "IT_ATTENTION_REQUIRED", submission });
          stopBatch(target.poNumber, "The transmission requires review. No remaining invoices were sent."); break;
        }
        workflow = await get810Workflow(activeLoadId, target.poNumber, { headers });
        if (!operationIsActive(operation)) return;
        replaceWorkflow(target.poNumber, workflow);
        if (workflow.workflowState === "SUBMITTING") { stopBatch(target.poNumber, "This invoice is still submitting. No remaining invoices were sent."); break; }
        if (TERMINAL_STOP_STATES.has(workflow.workflowState)) { stopBatch(target.poNumber, "The transmission requires review. No remaining invoices were sent."); break; }
        if (!SAFE_AFTER_SEND.has(workflow.workflowState)) { stopBatch(target.poNumber, "The workflow did not reach a safe post-send state. No remaining invoices were sent."); break; }
      }
    } catch (exception) {
      if (!(await permissionFailure(exception))) stopBatch(targets[0]?.poNumber || "Invoice", exception?.message || "No remaining invoices were sent.");
    } finally {
      if (mounted.current) setSelected([]);
      finishMutation(operation);
    }
  };

  const runRows = useMemo(() => runPoNumbers.map((po) => rows.find((row) => row.poNumber === po)).filter(Boolean), [rows, runPoNumbers]);
  const pendingKey = runRows.filter((row) => BATCH_810_POLL_STATES.has(row.workflowState)).map((row) => row.poNumber).join("|");
  useEffect(() => {
    if (!pendingKey || !activeLoadId) return undefined;
    const pollLoad = activeLoadId;
    const pollRequest = requestId.current;
    const poll = async () => {
      if (pollActive.current || mutationActive.current) return;
      pollActive.current = true;
      try {
        const pending = pendingKey.split("|");
        const values = await Promise.all(pending.map((po) => get810Workflow(pollLoad, po, { headers })));
        if (!mounted.current || pollLoad !== activeLoadId || pollRequest !== requestId.current) return;
        values.forEach((workflow, index) => replaceWorkflow(pending[index], workflow, pollLoad, pollRequest));
      } catch (exception) {
        if (mounted.current && pollLoad === activeLoadId && pollRequest === requestId.current) setError(exception?.message || "Unable to refresh the Walmart 810 batch status.");
      } finally { pollActive.current = false; }
    };
    const timer = window.setInterval(poll, 5000);
    return () => window.clearInterval(timer);
  }, [activeLoadId, headers, pendingKey, replaceWorkflow]);

  const summary = useMemo(() => runRows.reduce((result, row) => {
    if (row.workflowState === "COMPLETED") result.accepted += 1;
    else if (BATCH_810_POLL_STATES.has(row.workflowState)) result.waiting += 1;
    else if (row.workflowState === "WALMART_REJECTED") result.rejected += 1;
    else if (row.workflowState === "IT_ATTENTION_REQUIRED") result.attention += 1;
    return result;
  }, { accepted: 0, waiting: 0, rejected: 0, attention: 0 }), [runRows]);

  return <section className="space-y-5" aria-labelledby="batch-810-title">
    <header><h2 id="batch-810-title" className="text-xl font-semibold">Batch 810</h2><p className="mt-1 text-sm text-slate-600">Load the authoritative shipped PO population, select eligible invoices, and review every document before sequential Send.</p></header>
    <form className="grid gap-3 rounded-lg border bg-white p-4 sm:grid-cols-[1fr_1fr_auto]" onSubmit={(event) => { event.preventDefault(); loadBatch(loadId); }}>
      <label className="text-sm font-medium">Load ID<input aria-label="810 Batch Load ID" disabled={Boolean(busyAction)} value={loadId} onChange={(event) => changeLoadId(event.target.value)} className="mt-1 w-full rounded border px-3 py-2 disabled:bg-slate-100"/></label>
      <label className="text-sm font-medium">Invoice Date<input aria-label="810 Invoice Date" type="date" required disabled={Boolean(busyAction)} value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} className="mt-1 w-full rounded border px-3 py-2 disabled:bg-slate-100"/></label>
      <div className="flex items-end"><button type="submit" disabled={Boolean(busyAction) || loading || !loadId.trim()} className="w-full rounded bg-blue-700 px-4 py-2 text-white disabled:opacity-50">{loading ? "Looking up..." : "Lookup Load"}</button></div>
    </form>
    {error && <div role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</div>}
    {!canWrite && rows.length > 0 && <div className="rounded bg-slate-50 p-3 text-sm">Your Walmart 810 permission is read-only. You can view invoice workflow status but cannot generate or send invoices.</div>}
    {rows.length > 0 && <section className="rounded-lg border bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-lg font-semibold">Load {activeLoadId}</h2>{canWrite && <button type="button" disabled={Boolean(busyAction) || !selected.length || !activeLoadId} onClick={generateAndReview} className="rounded bg-blue-700 px-4 py-2 text-white disabled:opacity-50">{busyAction === "prepare" ? "Preparing batch..." : "Generate & Review Selected"}</button>}</div>
      <div className="mt-3 overflow-x-auto"><table className="min-w-full text-left text-sm">
        <thead><tr className="border-b bg-slate-50"><th className="p-2"><label className="flex items-center gap-2"><input aria-label="Select all eligible 810s" type="checkbox" disabled={!canWrite || !selectable.length || Boolean(busyAction)} checked={allSelected} onChange={() => setSelected(allSelected ? [] : selectable)}/>Select</label></th><th className="p-2">PO</th><th className="p-2">Invoice #</th><th className="p-2">Quantity</th><th className="p-2">Gross</th><th className="p-2">Allowance</th><th className="p-2">Invoice Total</th><th className="p-2">Status</th></tr></thead>
        <tbody>{rows.map((row) => {
          const eligible = is810Selectable(row, canWrite);
          const issues = row.preview?.blockingIssues || row.reasons || [];
          return <tr key={row.poNumber} className="border-b"><td className="p-2"><input aria-label={`Select PO ${row.poNumber}`} type="checkbox" disabled={!eligible || Boolean(busyAction)} checked={selected.includes(row.poNumber)} onChange={() => toggle(row.poNumber)}/></td><td className="p-2 font-medium">{shown(row.poNumber)}</td><td className="p-2">{row.generation?.invoiceNumber || "Assigned on Generate"}</td><td className="p-2">{shown(authoritativeQuantity(row.preview?.lines))}</td><td className="p-2">{shown(row.preview?.totals?.gross)}</td><td className="p-2">C000 {shown(row.preview?.totals?.c000)} / I570 {shown(row.preview?.totals?.i570)}</td><td className="p-2">{shown(row.preview?.totals?.invoiceTotal)}</td><td className="p-2"><span>{batch810StateLabel(row.workflowState || row.classification)}</span>{issues.length > 0 && <div className="mt-1 text-xs text-amber-800">{issues.map(issueLabel).join(", ")}</div>}</td></tr>;
        })}</tbody>
      </table></div>
    </section>}
    {statusVisible && runRows.length > 0 && <div className={`rounded p-4 ${halted ? "bg-red-50 text-red-900" : summary.waiting ? "bg-amber-50 text-amber-900" : "bg-emerald-50 text-emerald-900"}`}><h2 className="font-semibold">{halted ? "Batch Stopped for Safety" : summary.waiting ? "Batch In Progress" : "Batch Complete"}</h2><p className="mt-1 text-sm">Accepted: {summary.accepted} · Waiting: {summary.waiting} · Rejected: {summary.rejected} · IT Attention: {summary.attention}</p></div>}
    {statusVisible && <StatusTable rows={runRows} preparedTrace={preparedTrace}/>}
    {reviewOpen && reviewRows.length > 0 && <ReviewModal rows={reviewRows} busy={busyAction === "send"} onCancel={() => { if (!mutationActive.current) { setReviewOpen(false); setReviewRows([]); } }} onConfirm={sendSelected}/>}
  </section>;
}
